import React, { useState, useCallback } from 'react';
import { Form, Input, Select, DatePicker, Button, Spin, Switch, Divider, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { getCropNamesApi, createCropApi, updateCropApi, changeCropStatusApi } from '../../api/crops';
import { AREA_UNITS, CROP_NAME_IMAGE } from '../../utils/constants';
import dayjs from 'dayjs';

const AREA_UNIT_OPTIONS = Object.entries(AREA_UNITS).map(([k, v]) => ({ value: k, label: v }));

const label = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const hint = { fontSize: 12, color: '#9ca3af', fontWeight: 400, marginLeft: 6 };
const req  = { color: '#ef4444', marginLeft: 4, fontWeight: 700 };

export default function AddCropPage() {
  const navigate = useNavigate();
  const { farmId } = useParams();
  const location = useLocation();
  const initialValues  = location.state?.editData ?? null;
  const plots          = location.state?.plots ?? [];
  const selectedPlotId = location.state?.selectedPlotId ?? null;
  const selectedPlot   = location.state?.selectedPlot ?? null;
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [cropOptions, setCropOptions] = useState([]);
  const [searching, setSearching]     = useState(false);
  const [commonId, setCommonId]       = useState(null);
  const [harvestDate, setHarvestDate] = useState(null);
  const [sowingDate, setSowingDate]   = useState(null);
  const [isHarvested, setIsHarvested] = useState(false);
  const [saving, setSaving]           = useState(false);

  React.useEffect(() => {
    if (initialValues) {
      const hd = initialValues.harvest_date ? dayjs(initialValues.harvest_date) : null;
      const sd = initialValues.crop_date ? dayjs(initialValues.crop_date) : null;
      const harvested = initialValues.status === 0 || initialValues.status === '0';
      setHarvestDate(hd);
      setSowingDate(sd);
      setIsHarvested(harvested);
      form.setFieldsValue({
        cropName:     initialValues.crop_name || initialValues.crop,
        verity:       initialValues.verity || '',
        area:         initialValues.crop_area || initialValues.area,
        areaUnit:     String(initialValues.crop_area_unit ?? initialValues.area_unit ?? '3'),
        date:         initialValues.crop_date ? dayjs(initialValues.crop_date) : null,
        plotId:       initialValues.plot_id || null,
        harvest_date: hd,
        status:       harvested,
      });
      setCommonId(initialValues.common_id || null);
    } else {
      form.setFieldsValue({
        plotId:   selectedPlotId || undefined,
        areaUnit: String(selectedPlot?.seted_area_unit || '3'),
        area:     selectedPlot?.seted_area || undefined,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchCrops = useCallback(async (val = '') => {
    setSearching(true);
    try {
      const res = await getCropNamesApi({ lang: i18n.language, crop: val });
      setCropOptions(
        (res.data || []).map((c) => ({
          value:    c.name,
          label:    c.name,
          commonId: c.common_id,
          image:    c.image || null,
        }))
      );
    } catch {} finally { setSearching(false); }
  }, []);

  const handlePlotChange = useCallback((plotId) => {
    const plot = plots?.find((p) => p.id === plotId);
    if (plot) {
      form.setFieldsValue({
        area:     plot.seted_area || undefined,
        areaUnit: String(plot.seted_area_unit || '3'),
      });
    }
  }, [plots, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        cropName:     values.cropName,
        verity:       values.verity || '',
        area:         values.area,
        areaUnit:     values.areaUnit || '3',
        farm:         farmId,
        date:         values.date ? values.date.format('YYYY-MM-DD') : '',
        plotId:       selectedPlotId || values.plotId || null,
        hasPlot:      false,
        plotName:     '',
        plotPath:     null,
        sqmArea:      '',
        common_id:    commonId,
        harvest_date: values.harvest_date ? values.harvest_date.format('YYYY-MM-DD') : '',
        status:       values.status ? 0 : 1,
      };

      setSaving(true);
      if (initialValues?.id) {
        const editPayload = { ...payload, cropId: initialValues.id };
        if (!editPayload.harvest_date) delete editPayload.harvest_date;
        await updateCropApi(editPayload);
        if (payload.status !== undefined && payload.status !== initialValues.status) {
          await changeCropStatusApi({ crop_id: initialValues.id, status: payload.status });
        }
        message.success('Crop updated!');
      } else {
        await createCropApi(payload);
        message.success('Crop added!');
      }
      navigate(-1);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(initialValues?.id ? 'Failed to update crop' : 'Failed to add crop');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ margin: -24, minHeight: 'calc(100vh - 64px)', background: '#fff' }}>

      {/* ── Sticky page header ── */}
      <div style={{
        position: 'sticky', top: 64, zIndex: 100,
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 40px',
        height: 56,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, color: '#6b7280', display: 'flex', alignItems: 'center' }}
          >
            <ArrowLeftOutlined style={{ fontSize: 15 }} />
          </button>
          <span style={{ color: '#9ca3af', fontSize: 13 }}>/</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
            {initialValues?.id ? 'Edit Crop' : 'Add Crop'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button
            type="primary" loading={saving} onClick={handleSave}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a', fontWeight: 600 }}
          >
            Save
          </Button>
        </div>
      </div>

      {/* ── Form body ── */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 40px 80px' }}>

        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
          {initialValues?.id ? 'Edit Crop' : 'Add Crop'}
        </h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 36px' }}>
          Fill in the crop details to record it on your farm.
        </p>

        <Form form={form} layout="vertical" size="large">

          {/* ── Crop Name + Variety ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
            <div>
              <label style={label}>{t('crop_name')}<span style={req}>*</span></label>
              <Form.Item name="cropName" rules={[{ required: true, message: 'Select a crop' }]} style={{ marginBottom: 0 }}>
                {initialValues?.id ? (
                  <Input disabled style={{ borderRadius: 8 }} />
                ) : (
                  <Select
                    showSearch
                    filterOption={false}
                    onSearch={searchCrops}
                    onFocus={() => { if (cropOptions.length === 0) searchCrops(''); }}
                    onSelect={(_, opt) => setCommonId(opt.commonId)}
                    options={cropOptions}
                    optionRender={(opt) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {opt.data.image ? (
                          <img src={CROP_NAME_IMAGE(opt.data.image)} alt={opt.data.label} style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <span style={{ width: 28, textAlign: 'center', flexShrink: 0 }}>🌱</span>
                        )}
                        <span>{opt.data.label}</span>
                      </div>
                    )}
                    placeholder="Search or select crop..."
                    notFoundContent={searching ? <Spin size="small" /> : 'No crops found'}
                    listHeight={320}
                  />
                )}
              </Form.Item>
            </div>
            <div>
              <label style={label}>Variety<span style={req}>*</span></label>
              <Form.Item name="verity" rules={[{ required: true, message: 'Enter variety' }]} style={{ marginBottom: 0 }}>
                <Input placeholder="e.g. BT, Hybrid, Local..." style={{ borderRadius: 8 }} />
              </Form.Item>
            </div>
          </div>

          <Divider style={{ margin: '0 0 28px' }} />

          {/* ── Plot & Area ── */}
          {plots?.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <label style={label}>Plot<span style={req}>*</span></label>
              <Form.Item name="plotId" rules={[{ required: true, message: 'Select a plot' }]} style={{ marginBottom: 0 }}>
                <Select
                  allowClear
                  placeholder="Select plot (optional)"
                  options={plots.map((p) => ({ value: p.id, label: p.name }))}
                  onChange={handlePlotChange}
                />
              </Form.Item>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
            <div>
              <label style={label}>{t('area')}<span style={req}>*</span></label>
              <Form.Item name="area" rules={[{ required: true, message: 'Enter area' }]} style={{ marginBottom: 0 }}>
                <Input type="number" min={0} placeholder="e.g. 2" style={{ borderRadius: 8 }} />
              </Form.Item>
            </div>
            <div>
              <label style={label}>{t('area_unit')}</label>
              <Form.Item name="areaUnit" style={{ marginBottom: 0 }}>
                <Select options={AREA_UNIT_OPTIONS} />
              </Form.Item>
            </div>
          </div>

          <Divider style={{ margin: '0 0 28px' }} />

          {/* ── Dates ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
            <div>
              <label style={label}>{t('sowing_date')}<span style={req}>*</span></label>
              <Form.Item name="date" rules={[{ required: true, message: 'Select sowing date' }]} style={{ marginBottom: 0 }}>
                <DatePicker
                  style={{ width: '100%', borderRadius: 8 }}
                  onChange={(d) => {
                    setSowingDate(d);
                    setHarvestDate(null);
                    form.setFieldsValue({ harvest_date: null, status: false });
                  }}
                />
              </Form.Item>
            </div>
            <div>
              <label style={label}>Expected Harvest Date<span style={req}>*</span></label>
              <Form.Item name="harvest_date" rules={[{ required: true, message: 'Select expected harvest date' }]} style={{ marginBottom: 0 }}>
                <DatePicker
                  style={{ width: '100%', borderRadius: 8 }}
                  disabledDate={(d) => sowingDate && d && !d.isAfter(sowingDate, 'day')}
                  onChange={(d) => {
                    setHarvestDate(d);
                    if (!d) form.setFieldsValue({ status: false });
                  }}
                />
              </Form.Item>
            </div>
          </div>

          {/* ── Harvest toggle (edit only) ── */}
          {initialValues?.id && (
            <>
              <Divider style={{ margin: '0 0 28px' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: '#fafafa', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 40 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Mark as Harvested</div>
                  {!harvestDate && !isHarvested && (
                    <div style={{ fontSize: 12, color: '#d97706', marginTop: 3 }}>Select a harvest date first to enable this</div>
                  )}
                </div>
                <Form.Item name="status" valuePropName="checked" noStyle>
                  <Switch
                    disabled={!harvestDate && !isHarvested}
                    checkedChildren="Harvested"
                    unCheckedChildren="Active"
                  />
                </Form.Item>
              </div>
            </>
          )}

        </Form>

        {/* ── Actions ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
          <Button size="large" onClick={() => navigate(-1)} style={{ minWidth: 100, borderRadius: 8 }}>Cancel</Button>
          <Button
            size="large" type="primary" loading={saving} onClick={handleSave}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a', minWidth: 120, borderRadius: 8, fontWeight: 600 }}
          >
            Save Crop
          </Button>
        </div>

      </div>
    </div>
  );
}
